import { resolveDriveItemFromUrl } from "./excel";

/**
 * True only for http(s) URLs. Guards window.open() against javascript:/data:
 * URLs — a stored note field (e.g. a deck's webUrl) is attacker-influencable,
 * and window.open on a javascript: URL is an XSS sink.
 */
export function isSafeWebUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  try {
    const proto = new URL(url).protocol;
    return proto === "https:" || proto === "http:";
  } catch {
    return false;
  }
}

export interface PresentationMeta {
  driveItemId: string;
  name: string;
  webUrl: string;
}

/**
 * Build the Graph item path, handling the cross-tenant "{driveId}!{itemId}"
 * format the same way excel.ts does.
 */
function itemPath(driveItemId: string): string {
  return driveItemId.includes("!")
    ? `drives/${driveItemId.split("!")[0]}/items/${driveItemId}`
    : `me/drive/items/${driveItemId}`;
}

/**
 * Resolves a OneDrive/SharePoint sharing link to a PowerPoint presentation's
 * durable metadata (id, name, webUrl). Reuses the Excel link resolver, which
 * already handles the cross-tenant 308 → anonymous-retry path.
 */
export async function resolvePresentation(
  graphToken: string,
  sharingUrl: string
): Promise<PresentationMeta> {
  const driveItemId = await resolveDriveItemFromUrl(graphToken, sharingUrl);

  const res = await fetch(
    `https://graph.microsoft.com/v1.0/${itemPath(driveItemId)}?$select=id,name,webUrl,file`,
    { headers: { Authorization: `Bearer ${graphToken}` } }
  );

  if (!res.ok) {
    if (res.status === 401) {
      throw new Error(
        "MarginPro isn’t connected to your OneDrive yet (or the connection expired). Press Reconnect, then paste the link again."
      );
    }
    throw new Error(
      "We couldn’t open that file. Make sure it’s a valid PowerPoint you have access to."
    );
  }

  const data = await res.json();
  const name: string = data.name || "Presentation";
  if (!/\.pptx?$/i.test(name)) {
    throw new Error("That link isn’t a PowerPoint file. Please link a .pptx (or .ppt) file.");
  }

  return {
    driveItemId: data.id || driveItemId,
    name,
    webUrl: data.webUrl || sharingUrl,
  };
}

/**
 * Fetches a rendered cover-slide thumbnail URL for the presentation.
 * The returned URL is a short-lived pre-authenticated Graph URL (~hours), so
 * callers should fetch it fresh on mount rather than persisting it long-term.
 * Returns null if Graph can't produce a thumbnail (never throws for that).
 */
export async function fetchCoverThumbnail(
  graphToken: string,
  driveItemId: string
): Promise<string | null> {
  try {
    const res = await fetch(
      `https://graph.microsoft.com/v1.0/${itemPath(driveItemId)}/thumbnails/0`,
      { headers: { Authorization: `Bearer ${graphToken}` } }
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data?.large?.url || data?.medium?.url || data?.small?.url || null;
  } catch {
    return null;
  }
}

/**
 * Requests a short-lived, embeddable preview URL via Graph. The returned URL
 * renders an authenticated slide viewer (with its own next/prev controls),
 * suitable for an <iframe>. Short-lived (~minutes), so fetch on demand rather
 * than persisting. Returns null on failure (caller falls back to "open in
 * PowerPoint"). Part 2 of the PowerPoint link-in feature.
 */
export async function fetchPreviewUrl(
  graphToken: string,
  driveItemId: string
): Promise<string | null> {
  try {
    const res = await fetch(
      `https://graph.microsoft.com/v1.0/${itemPath(driveItemId)}/preview`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${graphToken}`, "Content-Type": "application/json" },
        body: "{}",
      }
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data?.getUrl || null;
  } catch {
    return null;
  }
}
